import { useState } from 'react';
import { Badge, Button, Card, ConfirmDialog, EmptyState, LoadingList, PageHeader } from '@timely/ui';
import { holds } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { Field, FormNotice } from './AuthLayout';
import { ApiError } from '../lib/api';
import {
    AI_PROVIDERS, VERIFICATION_WORDS, providerLabel, removeAiKey, saveAiKey, useAiCredential, verifyAiKey,
    type AiCredentialStatus, type Verification,
} from '../ai/useAiCredential';

/** A moment, in the company's zone, as "23 Sept 2026, 08:01". */
function momentIn(value: string | null, locale: string, timeZone: string): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    try {
        return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(d);
    } catch {
        return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
    }
}

/** The key's state as words with the settle square, beside the provider's name — not a pill. */
function StatusBadge({ status }: { status: AiCredentialStatus }) {
    if (!status.configured) return <Badge variant="text" dot>Not configured</Badge>;
    if (status.verificationStatus === 'verified') return <Badge variant="text" dot tone="pos">Connected</Badge>;
    if (status.verificationStatus === 'failed') return <Badge variant="text" dot tone="dang">Verification failed</Badge>;
    return <Badge variant="text" dot tone="warn">Saved · not verified yet</Badge>;
}

const verdict = (verification: Verification): string =>
    verification.ok ? 'The provider accepted the key.' : VERIFICATION_WORDS[verification.category];

/**
 * AI: the company's own key for AI features, and nothing else yet.
 *
 * **Optional.** Nothing in Timely Payment depends on it; the screen says so
 * before it asks for anything.
 *
 * **Write-only.** A saved key is never shown again — not its first characters,
 * not its last, not to the person who typed it. The field holds what is being
 * typed and is emptied the moment it is submitted, whether or not the save
 * works; a failed save means typing it again, which is the safer of the two.
 */
export function AiSettings() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const canManage = holds(context, 'integrations.manage');
    const { status, loading, error, reload } = useAiCredential(workspace.id);

    const [apiKey, setApiKey] = useState('');
    const [busy, setBusy] = useState<null | 'save' | 'verify' | 'remove'>(null);
    const [confirmRemove, setConfirmRemove] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const provider = AI_PROVIDERS[0];

    if (!canManage) {
        return (
            <Page width="form">
                <EmptyState
                    title="AI summaries are set up by administrators"
                    hint="Somebody who manages integrations can connect an AI key for this company."
                />
            </Page>
        );
    }

    const failed = (caught: unknown, fallback: string) =>
        setNotice({ tone: 'error', text: caught instanceof ApiError ? caught.message : fallback });

    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        const typed = apiKey;
        // Gone from the screen before the request leaves, whatever happens next.
        setApiKey('');
        if (!typed.trim()) {
            setNotice({ tone: 'error', text: 'Paste the API key first.' });
            return;
        }
        setBusy('save');
        setNotice(null);
        try {
            const result = await saveAiKey(workspace.id, provider.id, typed);
            setNotice({
                tone: result.verification.ok ? 'ok' : 'error',
                text: `${result.action === 'replaced' ? 'Key replaced.' : 'Key saved.'} ${verdict(result.verification)}`,
            });
        } catch (caught) {
            failed(caught, 'That key was not saved. Try again.');
        } finally {
            setBusy(null);
            void reload();
        }
    };

    const verify = async () => {
        setBusy('verify');
        setNotice(null);
        try {
            const { verification } = await verifyAiKey(workspace.id);
            setNotice({ tone: verification.ok ? 'ok' : 'error', text: verdict(verification) });
        } catch (caught) {
            failed(caught, 'We could not check the key just now.');
        } finally {
            setBusy(null);
            void reload();
        }
    };

    const remove = async () => {
        setBusy('remove');
        setNotice(null);
        try {
            const { removed } = await removeAiKey(workspace.id);
            setNotice({ tone: 'ok', text: removed ? 'The AI key was removed.' : 'There was no AI key to remove.' });
        } catch (caught) {
            failed(caught, 'The key was not removed. Try again.');
        } finally {
            setConfirmRemove(false);
            setBusy(null);
            void reload();
        }
    };

    return (
        <Page width="form">
            <PageHeader
                title="AI summaries"
                subtitle="Your company's own AI key, for credit summaries. Optional — everything else in Timely Payment works without it."
            />

            {notice && <div ><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}

            <Card className="mt-5 p-5">
                {loading ? (
                    <LoadingList label="Checking the AI key" rows={2} />
                ) : error ? (
                    <FormNotice tone="error">{error}</FormNotice>
                ) : (
                    <div>
                        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
                            <span className="text-subhead font-semibold text-label">{providerLabel(status.provider ?? provider.id)}</span>
                            <StatusBadge status={status} />
                        </div>
                        {status.configured ? (
                            <dl className="mt-3 grid grid-cols-terms gap-x-4 gap-y-1 text-body">
                                <dt className="text-label-3">API key</dt>
                                <dd className="text-label">Configured — it cannot be shown again</dd>
                                <dt className="text-label-3">Saved</dt>
                                <dd className="text-label">{momentIn(status.updatedAt, workspace.locale, workspace.timezone)}</dd>
                                <dt className="text-label-3">Last checked</dt>
                                <dd className="text-label">{momentIn(status.checkedAt, workspace.locale, workspace.timezone)}</dd>
                            </dl>
                        ) : (
                            <p className="mt-2 text-body text-label-2">
                                No AI key is saved. AI features stay off until one is added.
                            </p>
                        )}
                        {status.configured && status.verificationStatus === 'failed' && status.verificationCategory && (
                            <p className="mt-3 text-body text-dang" role="status">
                                {VERIFICATION_WORDS[status.verificationCategory]}
                            </p>
                        )}
                        {status.configured && (
                            <div className="mt-4 flex gap-2 flex-wrap">
                                <Button type="button" variant="secondary" onClick={verify} disabled={busy !== null} busy={busy === 'verify'}>
                                    {busy === 'verify' ? 'Checking…' : 'Verify connection'}
                                </Button>
                                <Button type="button" variant="danger" onClick={() => setConfirmRemove(true)} disabled={busy !== null}>
                                    Remove key
                                </Button>
                            </div>
                        )}
                    </div>
                )}
                {/* The key's state and the field that changes it are one thing: one surface, divided once. */}
                <div className="mt-5 pt-5 -mx-5 px-5 border-t border-separator">
                <form onSubmit={save} autoComplete="off">
                    <Field
                        label={status.configured ? 'Replace with a new API key' : 'API key'}
                        name="ai-api-key"
                        type="password"
                        autoComplete="new-password"
                        spellCheck={false}
                        value={apiKey}
                        onChange={e => setApiKey(e.target.value)}
                        hint={`Paste the key from Google AI Studio. It is encrypted before it is stored, `
                            + 'and it can never be shown again — not even to you. Replacing it needs the whole new key.'}
                    />
                    <Button type="submit" variant="primary" disabled={busy !== null} busy={busy === 'save'}>
                        {busy === 'save' ? 'Saving…' : status.configured ? 'Replace key' : 'Save key'}
                    </Button>
                </form>
                </div>
            </Card>

            <ConfirmDialog
                open={confirmRemove}
                title="Remove the AI key?"
                confirmLabel="Remove key"
                busy={busy === 'remove'}
                onConfirm={remove}
                onCancel={() => setConfirmRemove(false)}
            >
                The {providerLabel(status.provider)} key saved for {workspace.name} will be deleted. AI features
                stop until a new key is added. This cannot be undone — you would need to paste the key again.
            </ConfirmDialog>
        </Page>
    );
}
